/**
 * AnalyticsPage — /analytics (US-17-GR-24)
 *
 * Rebuilds the Reports & Analytics surface deleted from the user app by frontend
 * commit 9c418bd3f (US-24-GR-20) into the dedicated ChellessAdmin console. It
 * consumes the live admin metrics endpoints that had zero UI consumers:
 *   GET /api/admin/metrics                      (base KPI summary + engagement + first sale)
 *   GET /api/admin/metrics/retention?window=N   (Week 1/2/4 retention series)
 *   GET /api/admin/metrics/revenue              (tier / conversion / churn / LTV / CAC)
 *   GET /api/admin/metrics/nps                  (NPS score breakdown)
 *   GET /api/admin/metrics/outsourcing?window=N (marketplace health)
 *
 * The windowed panels (retention, outsourcing) refetch when the shared
 * TimeWindowSelector changes (7/30/90 pills, persisted to localStorage). The
 * revenue/NPS panel is window-independent (endpoints take no window).
 *
 * Access is gated by the AdminShell (useIsAdmin → NotAuthorizedPage) and the
 * server-side [Authorize(Roles = "Admin")] on every endpoint.
 */

import { useEffect, useState, useCallback, startTransition, type ReactNode } from 'react';
import { Users, UserCheck } from 'lucide-react';
import {
  TimeWindowSelector,
  loadStoredWindow,
  saveStoredWindow,
  type TimeWindow,
} from '../components/TimeWindowSelector';
import { RetentionEngagementPanel } from '../components/RetentionEngagementPanel';
import { RevenueCustomerHealthPanel } from '../components/RevenueCustomerHealthPanel';
import { DecisionsPerWeekTile } from '../components/DecisionsPerWeekTile';
import { FirstSalePanel } from '../components/FirstSalePanel';
import { OutsourcingMarketplacePanel } from '../components/OutsourcingMarketplacePanel';
import { AbbrLabel } from '../components/AbbrLabel';
import { ScopeNote } from '../components/ScopeNote';
import { getAdminMetrics, type AdminMetricsSummary } from '../services/adminMetricsService';

/**
 * "Active" means the user emitted a UsageEvent in the window (US-17-GR-35) —
 * the same partial rail as the Usage report: mostly best-effort browser
 * telemetry from the consumer web app, plus a small reliable SecondBrain
 * slice, and zero coverage of ChellessAdmin, IbaApp, ProviderConsole,
 * CustomerStatus, and Mobile. Activity on those surfaces is unmeasured here,
 * not zero.
 */
/**
 * Work item 17479. The "Total users" tile was the only one of the console's five user totals with
 * no scope caveat at all, and the five answer three genuinely different questions: the ProfileDB
 * identity roster (/users), the TaskManager subscription mirror (the billing tiles), and distinct
 * board owners (this tile and the /dashboard activity tile).
 *
 * A reader of /analytics had no way to know this one means board owners. Its neighbours on
 * /dashboard already say so at the point of display ("profiles with a board, not all users"), and
 * this now matches them rather than leaving the least-qualified figure the most prominent.
 *
 * Note the wording deliberately does NOT say "sampled". Work item 17480 fixed the truncation that
 * made this a sample of the first 10,000 boards; it is now a COUNT(DISTINCT ProfileId) over the
 * whole table, so the only caveat left to state is the population, not the sampling.
 */
const TOTAL_USERS_SCOPE_EXPLANATION =
  'Counts distinct board owners, not every registered account. Three different populations are ' +
  'called "users" across this console: the identity roster on /users (ProfileDB, the source of ' +
  'truth), the subscription mirror behind the billing tiles, and board owners here. See /users ' +
  'for the identity figure.';

const ACTIVE_USERS_SCOPE_EXPLANATION =
  "'Active' means the user emitted a UsageEvent — mostly best-effort browser telemetry from the " +
  'consumer web app, plus a small reliable SecondBrain slice. ChellessAdmin, IbaApp, ProviderConsole, ' +
  'CustomerStatus, and Mobile emit none of it, so activity there is unmeasured, not zero.';

/** Headline stat tile for totalUsers / activeUsersLast30Days (US-17-GR-28). */
function HeadlineTile({
  label,
  value,
  icon,
  scopeExplanation,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  /** When set, renders a ScopeNote badge next to the label (US-17-GR-35). */
  scopeExplanation?: string;
}) {
  return (
    <div className="flex items-center gap-3 p-4 bg-zinc-900 rounded-xl border border-zinc-800">
      <div className="p-2 rounded-lg bg-violet-500/10 text-violet-400">{icon}</div>
      <div>
        <p className="text-xl font-semibold text-zinc-100">{value.toLocaleString()}</p>
        <p className="text-xs text-zinc-500 flex items-center gap-1.5">
          {label}
          {scopeExplanation && <ScopeNote explanation={scopeExplanation} />}
        </p>
      </div>
    </div>
  );
}

export function AnalyticsPage() {
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(loadStoredWindow);
  const [summary, setSummary] = useState<AdminMetricsSummary | null>(null);

  const handleWindowChange = (w: TimeWindow) => {
    setTimeWindow(w);
    saveStoredWindow(w);
  };

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await getAdminMetrics());
    } catch {
      // Non-fatal — the headline strip simply stays hidden; each panel below
      // handles its own errors independently.
    }
  }, []);

  useEffect(() => {
    startTransition(() => { void loadSummary(); });
  }, [loadSummary]);

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-y-auto px-6 py-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-sm font-semibold text-zinc-100">Reports &amp; Analytics</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            PRD success metrics — retention, revenue &amp; <AbbrLabel term="NPS" />, and outsourcing marketplace health
          </p>
        </div>
        <TimeWindowSelector value={timeWindow} onChange={handleWindowChange} />
      </div>

      {/* Headline strip — totalUsers + activeUsersLast30Days (US-17-GR-28) */}
      {summary && (
        <div className="grid grid-cols-2 gap-3 sm:max-w-md mb-6">
          <HeadlineTile
            label="Total users"
            value={summary.totalUsers}
            icon={<Users className="w-4 h-4" />}
            scopeExplanation={TOTAL_USERS_SCOPE_EXPLANATION}
          />
          <HeadlineTile
            label="Active users (30d)"
            value={summary.activeUsersLast30Days}
            icon={<UserCheck className="w-4 h-4" />}
            scopeExplanation={ACTIVE_USERS_SCOPE_EXPLANATION}
          />
        </div>
      )}

      <div className="space-y-6">
        {/* Retention + Engagement (windowed) */}
        <section className="p-5 bg-zinc-900 rounded-xl border border-zinc-800">
          <RetentionEngagementPanel window={timeWindow} />
        </section>

        {/* Decisions per week (US-13-GR-111, spec 018 FR-12) */}
        <DecisionsPerWeekTile window={timeWindow} />

        {/* First sale (US-17-GR-45) — reads the summary already fetched above, so this adds
            no request. The block reached the wire from the day it was built and had no
            consumer; guarded because the type is optional, not because the server omits it. */}
        {summary?.firstSale && (
          <section className="p-5 bg-zinc-900 rounded-xl border border-zinc-800">
            <FirstSalePanel metrics={summary.firstSale} />
          </section>
        )}

        {/* Revenue + NPS (window-independent) */}
        <section className="p-5 bg-zinc-900 rounded-xl border border-zinc-800">
          <RevenueCustomerHealthPanel />
        </section>

        {/* Outsourcing marketplace (windowed) */}
        <section className="p-5 bg-zinc-900 rounded-xl border border-zinc-800">
          <OutsourcingMarketplacePanel window={timeWindow} />
        </section>
      </div>
    </main>
  );
}

export default AnalyticsPage;
